import { TeamCount } from "@redotech/account-common/connected-team";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import { RedoMerchantClientContext } from "@redotech/redo-merchant-app-common/client/context";
import { EffectApiClientContext } from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import { MerchantAppTopic } from "@redotech/redo-merchant-app-common/events/merchant-app-event-server";
import { MerchantAppEventServerContext } from "@redotech/redo-merchant-app-common/events/merchant-app-event-server-provider";
import { useSupportHelpdeskV2Enabled } from "@redotech/redo-merchant-app-common/setting/use-support-helpdesk-v2-enabled";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { getConnectedStores } from "@redotech/redo-merchant-login-app/client/auth";
import { TeamSwitcherLoginModal } from "@redotech/redo-merchant-login-app/team-switcher-login-modal";
import {
  ConnectedTeamData,
  CurrentTeamData,
  TeamDataContext,
  TeamSwitcherData,
} from "@redotech/redo-web/arbiter-components/navigation/common/team-switcher-data";
import { useQuery } from "@tanstack/react-query";
import { Effect } from "effect";
import { memo, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { resolveTeamDisplayName } from "./team-switcher-name";

export const TeamSwitcherService = memo(function TeamSwitcherService({
  children,
}: {
  children: React.ReactNode;
}) {
  const team = useRequiredContext(TeamContext);
  const [addStoreModalOpen, setAddStoreModalOpen] = useState(false);

  const client = useRequiredContext(RedoMerchantClientContext);
  const effectApi = useRequiredContext(EffectApiClientContext);
  const eventServer = useRequiredContext(MerchantAppEventServerContext);
  const { enabled: isV2, pending: isV2Pending } = useSupportHelpdeskV2Enabled();
  const [v1ConnectedTeamCounts, setV1ConnectedTeamCounts] = useState<
    Record<string, number>
  >({});
  const navigate = useNavigate();

  const connectedStores = useLoad(
    async (signal) => {
      if (team) {
        return await getConnectedStores(client, { signal });
      }
      return [];
    },
    [team],
  );

  const hasConnectedStores = (connectedStores.value?.length || 0) > 0;

  // v2: fetch connected-team ticket counts via the effect-api RPC, polling every 60s
  const { data: v2ConnectedTeamCounts } = useQuery({
    queryKey: ["connectedTeamsCounts"],
    queryFn: async () => {
      const rpcClient = effectApi.getClient();
      return await effectApi.runPromise(
        rpcClient.connectedTeamsCounts
          .getConnectedTeamsCounts({})
          .pipe(
            Effect.catchAll(() => Effect.succeed<Record<string, number>>({})),
          ),
      );
    },
    enabled: isV2 && !isV2Pending && hasConnectedStores,
    refetchInterval: 60_000,
    staleTime: 0,
  });

  const updateV1ConnectedTeamCounts = useHandler(
    (newTeamCount: TeamCount | null) => {
      if (newTeamCount) {
        setV1ConnectedTeamCounts((oldCounts) => ({
          ...oldCounts,
          [newTeamCount.teamId]: newTeamCount.count,
        }));
      }
    },
  );

  // v1: use the SSE-based event server subscription
  useEffect(() => {
    if (hasConnectedStores && !isV2Pending && !isV2) {
      const unlistenCallback = eventServer.subscribeAndCallOnce({
        topic: MerchantAppTopic.CONNECTED_TEAMS_COUNTS,
        callback: (value) => updateV1ConnectedTeamCounts(value),
      });
      return () => unlistenCallback();
    }
    return;
  }, [
    hasConnectedStores,
    updateV1ConnectedTeamCounts,
    eventServer,
    isV2,
    isV2Pending,
  ]);

  const connectedTeamCounts = useMemo(
    () => (isV2 ? (v2ConnectedTeamCounts ?? {}) : v1ConnectedTeamCounts),
    [isV2, v2ConnectedTeamCounts, v1ConnectedTeamCounts],
  );

  const handleTeamSwitch = useHandler((teamId: string) => {
    if (teamId === team._id) {
      return;
    }
    navigate(`/stores/${teamId}`);
    navigate(0);
  });

  const countTotal = Object.values(connectedTeamCounts).reduce(
    (sum: number, count: number) => sum + count,
    0,
  );

  const data: TeamSwitcherData = useMemo(() => {
    const currentTeamData: CurrentTeamData = {
      teamId: team._id,
      name: resolveTeamDisplayName(team),
      logoUrl: team.settings.brandKit?.images.logoUrl,
    };

    const connectedTeamData: ConnectedTeamData[] = (
      connectedStores.value || []
    ).map((connectedStore) => {
      return {
        teamId: connectedStore.teamId,
        name: resolveTeamDisplayName(connectedStore),
        logoUrl: connectedStore.logoUrl,
        ticketCount: connectedTeamCounts[connectedStore.teamId] || 0,
      };
    });

    return {
      connectedStoresHaveTickets: countTotal > 0,
      connectedStores: connectedTeamData,
      setCurrentTeam: handleTeamSwitch,
      currentTeam: currentTeamData,
      addStore: () => setAddStoreModalOpen(true),
    };
  }, [
    countTotal,
    connectedStores.value,
    connectedTeamCounts,
    team,
    handleTeamSwitch,
  ]);

  return (
    <TeamDataContext.Provider value={data}>
      <>
        {children}
        <TeamSwitcherLoginModal
          open={addStoreModalOpen}
          setOpen={setAddStoreModalOpen}
        />
      </>
    </TeamDataContext.Provider>
  );
});
